import React from "react";

/**
 * JsonView - JSON textarea editor with validation
 * 
 * @param {Object} props
 * @param {string} props.value - JSON string or plain text
 * @param {Function} props.onChange - Callback(newValue: string) on content change
 * @param {Function} props.onError - Callback(error: string|null) on validation change
 * @param {string} props.fileType - File type ('tools', 'policy', 'scopes', 'prompt')
 * @param {boolean} props.readOnly - Disable editing
 * @param {'json'|'yaml'} props.language - JSON syntax checks, or unparsed YAML text
 * @param {string} props.id - Optional textarea ID for an associated label
 * @param {string} props.ariaLabel - Optional accessible textarea name
 * @param {string} props.placeholder - Optional document-specific placeholder
 * 
 * Validation:
 * - Attempts JSON.parse on change
 * - Validates tools.json structure only when fileType === 'tools'
 * - Calls onError with message on invalid JSON
 * - Calls onError(null) on valid JSON
 */
const JsonView = ({
  value,
  onChange,
  onError,
  fileType = "tools",
  readOnly = false,
  language = "json",
  id,
  ariaLabel,
  placeholder = '{\n  "tools": [\n    "tool_name_1",\n    "tool_name_2"\n  ]\n}',
}) => {
  const validateToolsJson = (parsed) => {
    const hasTools = "tools" in parsed;
    const hasAgents = "agents" in parsed;

    if (hasTools && hasAgents) {
      return "tools.json cannot have both 'tools' and 'agents' keys";
    }

    if (!hasTools && !hasAgents) {
      return "tools.json must have either 'tools' or 'agents' key";
    }

    // Validate arrays and contents
    if (hasTools) {
      if (!Array.isArray(parsed.tools)) {
        return "tools.json 'tools' must be an array";
      }
      // Empty tools array is valid (no filter)
      if (parsed.tools.length > 0 && !parsed.tools.every(t => typeof t === "string" && t.trim().length > 0)) {
        return "tools.json 'tools' must contain non-empty strings";
      }
    }

    if (hasAgents) {
      if (!Array.isArray(parsed.agents)) {
        return "tools.json 'agents' must be an array";
      }
      if (parsed.agents.length === 0) {
        return "tools.json 'agents' array cannot be empty";
      }
      if (!parsed.agents.every(a => typeof a === "string" && a.trim().length > 0)) {
        return "tools.json 'agents' must contain non-empty strings";
      }
    }

    return null;
  };

  const validateContent = (content) => {
    let error = null;
    
    // Validate JSON syntax
    if (language === "json" && content.trim()) {
      try {
        const parsed = JSON.parse(content);
        
        // Only validate tools.json structure for tools files
        if (fileType === "tools") {
          error = validateToolsJson(parsed);
        }
      } catch (err) {
        error = `Invalid JSON: ${err.message}`;
      }
    }

    return error;
  };

  const localError = validateContent(value);

  const handleChange = (e) => {
    const content = e.target.value;
    const error = validateContent(content);
    if (onError) {
      onError(error);
    }

    if (onChange) {
      onChange(content);
    }
  };

  return (
    <div className="w-full">
      <textarea
        id={id}
        aria-label={ariaLabel}
        aria-invalid={Boolean(localError)}
        value={value}
        onChange={handleChange}
        readOnly={readOnly}
        rows={12}
        className={`w-full px-4 py-3 font-mono text-sm border rounded-lg bg-fill-white dark:bg-fill-deep-surface text-ink-ink dark:text-ink-white focus:ring-2 focus:ring-focus-primary focus:border-transparent resize-y ${
          localError
            ? "border-edge-red-400 dark:border-edge-red-500"
            : "border-edge-gray-300 dark:border-edge-gray-600"
        }`}
        placeholder={placeholder}
      />
      {localError && (
        <p className="text-xs text-ink-red-500 dark:text-ink-red-400 mt-2">{localError}</p>
      )}
    </div>
  );
};

export default JsonView;
